/**
 * The slate: up to six presets, each with a title, waiting to become one
 * printed page.
 *
 * A preset with no name is a QR code you cannot tell apart from five others in
 * a wet pocket, so the title is a first-class field rather than an afterthought
 * — editable in place, defaulted to something sensible, never empty.
 */

import type { ReactNode } from "react";

import { MAX_SLOTS, type CompiledSlot } from "../lib/shelf";
import { QrCode } from "./QrCode";

interface ShelfProps {
  slots: readonly CompiledSlot[];
  onRename: (id: string, title: string) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onLoad: (id: string) => void;
  onExport: () => void;
}

export function Shelf({ slots, onRename, onRemove, onMove, onLoad, onExport }: ShelfProps) {
  const empties = Math.max(0, MAX_SLOTS - slots.length);
  const printable = slots.filter((s) => s.ok).length;

  return (
    <section className="mt-6 rounded-[2px] border-2 border-graphite bg-paper p-4">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-line pb-2.5">
        <div>
          <h2 className="text-[24px] font-bold uppercase leading-none">The slate</h2>
          <p className="mt-1 max-w-[52ch] text-[13px] text-graphite-2">
            Up to six presets on one printable page. Cut them up, laminate them, clip them to a BCD
            — a code you can scan on the boat is worth more than one saved in an app you cannot
            open with wet hands.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <span className="font-mono text-xs text-graphite-2">
            {slots.length} / {MAX_SLOTS}
          </span>
          <button
            type="button"
            disabled={printable === 0}
            onClick={onExport}
            className="cursor-pointer rounded-[2px] border-none bg-deep px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-paper disabled:cursor-not-allowed disabled:opacity-40"
          >
            Print slate as PDF
          </button>
        </div>
      </div>

      <ol className="m-0 mt-3.5 grid list-none grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-2.5 p-0">
        {slots.map((slot, i) => (
          <li
            key={slot.id}
            className="grid grid-cols-[64px_1fr] gap-2.5 rounded-[2px] border border-slate-line bg-field p-2.5"
          >
            <div>
              <QrCode payload={slot.payload} className="[&_svg]:block [&_svg]:h-auto [&_svg]:w-full" />
              {!slot.ok && (
                <p className="m-0 mt-1 text-[10px] font-semibold uppercase text-flag">Has errors</p>
              )}
            </div>

            <div className="min-w-0">
              <label className="sr-only" htmlFor={`title-${slot.id}`}>
                Preset title
              </label>
              <input
                id={`title-${slot.id}`}
                value={slot.title}
                maxLength={40}
                onChange={(e) => onRename(slot.id, e.target.value)}
                onBlur={(e) => {
                  if (!e.target.value.trim()) onRename(slot.id, `Preset ${i + 1}`);
                }}
                className="w-full border-0 border-b border-dotted border-slate-line bg-transparent px-0 py-0.5 font-display text-[17px] font-semibold uppercase tracking-[0.02em] text-graphite focus:border-solid focus:border-smb focus:outline-none"
              />
              <p className="m-0 mt-1 text-[11px] leading-snug text-graphite-2">{slot.summary}</p>
              <p className="m-0 mt-0.5 font-mono text-[10px] text-graphite-2">
                {slot.conditions.camera} · {slot.payload.length} ch
              </p>

              <div className="mt-1.5 flex flex-wrap gap-1">
                <SlotButton onClick={() => onLoad(slot.id)}>Edit</SlotButton>
                <SlotButton onClick={() => onMove(slot.id, -1)} disabled={i === 0} label="Move earlier">
                  ←
                </SlotButton>
                <SlotButton
                  onClick={() => onMove(slot.id, 1)}
                  disabled={i === slots.length - 1}
                  label="Move later"
                >
                  →
                </SlotButton>
                <SlotButton onClick={() => onRemove(slot.id)} danger>
                  Remove
                </SlotButton>
              </div>
            </div>
          </li>
        ))}

        {Array.from({ length: empties }, (_, i) => (
          <li
            key={`empty-${i}`}
            className="grid min-h-[104px] place-items-center rounded-[2px] border border-dashed border-slate-line p-2.5 text-center text-[11px] leading-snug text-graphite-2"
          >
            Empty slot {slots.length + i + 1}
            <br />
            Build a preset above, then “Add to slate”.
          </li>
        ))}
      </ol>
    </section>
  );
}

function SlotButton({
  children,
  onClick,
  disabled,
  danger,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={[
        "cursor-pointer rounded-[2px] border px-1.5 py-0.5 font-display text-[12px] font-semibold uppercase tracking-[0.06em] disabled:cursor-not-allowed disabled:opacity-30",
        danger ? "border-flag text-flag" : "border-graphite-2 text-graphite-2",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
